import { gql } from '@apollo/client';

export const GET_ALL_CARS = gql`
  query GetCars($type: ProductType) {
    cars(type: $type) {
      id
      name
      displayName
      weekdayPrice
      weekendPrice
      holidayPrice
      monthlyPrice
      monthlyRentalIsOn
      isProtectionDeclinable
      monthlyPrice2Months
      monthlyPrice3Months
      gas
      gearType
      thumbnailUrl
      year
      model
      make
      seats
      images {
        imageUrl
      }
      priceCalendar {
        date
        price
        hourPrice
        isAvailable
      }
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      isCreated
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      category
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
      deposit
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      ownerId
      locationId
      ageLimit
      features
      minimumRentalDays
      maximumRentalDays
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
      isFavorite
    }
  }
`;

/** Legacy unbounded catalog. Homepage must use [GET_FEATURED_CARS] instead. */
export const GET_ACTIVE_CARS = gql`
  query GetActiveCars($type: ProductType, $siteHost: String) {
    activeCars(type: $type, siteHost: $siteHost) {
      id
      name
      displayName
      weekdayPrice
      weekendPrice
      holidayPrice
      monthlyPrice
      monthlyRentalIsOn
      isProtectionDeclinable
      monthlyPrice2Months
      monthlyPrice3Months
      gas
      gearType
      thumbnailUrl
      year
      model
      make
      seats
      images {
        imageUrl
      }
      priceCalendar {
        date
        price
        hourPrice
        isAvailable
      }
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      isCreated
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      category
      status
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
      deposit
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      ownerId
      locationId
      ageLimit
      features
      minimumRentalDays
      maximumRentalDays
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
      isFavorite
    }
  }
`;

/**
 * Guest home Featured Cars — lean carousel row only.
 * Do not select priceCalendar, images, owner, or bookings.
 */
export const GET_FEATURED_CARS = gql`
  query GetFeaturedCars($input: GetFeaturedCarsInput) {
    getFeaturedCars(input: $input) {
      id
      make
      model
      year
      category
      weekdayPrice
      monthlyPrice
      monthlyRentalIsOn
      thumbnailUrl
      isCreated
      tripSettings {
        maximumRentalDays
        displayPrices {
          oneWeek
        }
      }
    }
  }
`;

/**
 * Full AddNewCar selection — avoid on List-your-car → VIN (triggers heavy
 * location/owner trees). Prefer {@link ADD_NEW_CAR_DRAFT} for the add flow.
 */
export const ADD_NEW_CAR = gql`
  mutation AddNewCar($carInputData: NewCarInput!) {
    addNewCar(newCarData: $carInputData) {
      id
      name
      displayName
      holidayPrice
      weekdayPrice
      weekendPrice
      monthlyPrice
      monthlyRentalIsOn
      isProtectionDeclinable
      monthlyPrice2Months
      monthlyPrice3Months
      thumbnailUrl
      gearType
      year
      gas
      seats
      model
      make
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      images {
        imageUrl
      }
      isCreated
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
      deposit
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
      }
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      ownerId
      locationId
      ageLimit
      features
      category
      latitude
      longitude
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
    }
  }
`;

/**
 * Resume unfinished add-listing for the authenticated host.
 * getMyCarsV2 / cars() exclude isCreated=false — use this instead of GET_ALL_CARS.
 */
export const GET_MY_DRAFT_CAR = gql`
  query GetMyDraftCar {
    getMyDraftCar {
      id
      name
      displayName
      vin
      make
      model
      year
      seats
      gearType
      gas
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      category
      isCreated
      locationId
      ownerId
      currentLocation
      weekdayPrice
      weekendPrice
      holidayPrice
      hourlyRentalIsOn
      weekdayHrPrice
      weekendHrPrice
      holidayHrPrice
      monthlyRentalIsOn
      monthlyPrice
      monthlyPrice2Months
      monthlyPrice3Months
      thumbnailUrl
      originalPrice
      productTypeDetails {
        id
        type
      }
      location {
        id
        address
        city
        state
        zipCode
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
    }
  }
`;

/** Flutter-parity lean draft create — no nested location/owner/discount trees. */
export const ADD_NEW_CAR_DRAFT = gql`
  mutation AddNewCarDraft($carInputData: NewCarInput!) {
    addNewCar(newCarData: $carInputData) {
      id
      name
      displayName
      vin
      make
      model
      year
      seats
      gearType
      gas
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      category
      isCreated
      locationId
      ownerId
      weekdayPrice
      weekendPrice
      holidayPrice
      thumbnailUrl
      productTypeDetails {
        id
        type
      }
    }
  }
`;

export const UPDATE_CAR = gql`
  mutation UpdateCar($id: String!, $updateCarInput: UpdateCarInput!) {
    updateCar(id: $id, updateCarInput: $updateCarInput) {
      id
      name
      displayName
      holidayPrice
      weekdayPrice
      weekendPrice
      monthlyPrice
      monthlyRentalIsOn
      isProtectionDeclinable
      monthlyPrice2Months
      monthlyPrice3Months
      thumbnailUrl
      gearType
      year
      gas
      seats
      model
      make
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      images {
        imageUrl
      }
      isCreated
      status
      priceCalendar {
        date
        price
        hourPrice
        isAvailable
      }
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
      deposit
      dailyTripFee
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      ownerId
      locationId
      latitude
      longitude
      ageLimit
      features
      category
      minimumRentalDays
      maximumRentalDays
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
    }
  }
`;

export const GET_CAR = gql`
  query GetCar($id: String!) {
    car(id: $id) {
      id
      name
      freeCancellationAdvanceHours
      bookableOnThisSite {
        bookable
        reason
        message
      }
      tripSettings {
        hourlyRentalOffered
        monthlyRentalOffered
        discount
        protectionDeclinable
        minimumAge
        advanceNoticeHours
        tripBufferHours
        minimumRentalDays
        maximumRentalDays
        unlimitedMileage
        milesPerDay
        overagePerMile
        deposit
        guestInstructions
        displayPrices {
          threeDays
          oneWeek
          twoWeeks
          threeWeeks
        }
      }
      displayName
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
      hourlyRentalIsOn
      weekdayHrPrice
      weekendHrPrice
      holidayHrPrice
      weekdayPrice
      weekendPrice
      holidayPrice
      monthlyPrice
      monthlyRentalIsOn
      monthlyPrice2Months
      monthlyPrice3Months
      gas
      gearType
      thumbnailUrl
      year
      model
      make
      seats
      images {
        imageUrl
      }
      priceCalendar {
        date
        price
        hourPrice
        isAvailable
      }
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      isCreated
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      status
      description
      guestInstructions
      category
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
      deposit
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
        operatingHours {
          id
          PickupLocationType
          alwaysAvailable
          sundayAvailable
          sundayAlwaysAvailable
          sundayStart
          sundayEnd
          mondayAvailable
          mondayAlwaysAvailable
          mondayStart
          mondayEnd
          tuesdayAvailable
          tuesdayAlwaysAvailable
          tuesdayStart
          tuesdayEnd
          wednesdayAvailable
          wednesdayAlwaysAvailable
          wednesdayStart
          wednesdayEnd
          thursdayAvailable
          thursdayAlwaysAvailable
          thursdayStart
          thursdayEnd
          fridayAvailable
          fridayAlwaysAvailable
          fridayStart
          fridayEnd
          saturdayAvailable
          saturdayAlwaysAvailable
          saturdayStart
          saturdayEnd
        }
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      bookings {
        id
        startAt
        endAt
        guestId
        hostId
        pickupLocationId
        returnLocationId
        receipt {
          pricePerDay
          totalDays
          totalPrice
          extraMilages
          perExtraMileCost
          tripFee
          insurancePerDay
          insuranceByTrip
          subtotal
          paymentMethod
          taxRate
          tax
          balance
          last4digitOfCard
        }
        status
      }
      availabilities {
        id
        startAt
        endAt
        reason
        note
        createdAt
      }
      dailyTripFee
      createdAt
      ageLimit
      features
      category
      mileage
      latitude
      longitude
      minimumRentalDays
      maximumRentalDays
      dealer {
        id
        name
        phone
        email
      }
      isFavorite
      averageRating
      totalRatings
    }
  }
`;

export const UPDATE_PRICE_CALENDAR = gql`
  mutation UpdatePriceCalendar($carId: String!, $updatePriceCalendarData: [PriceCalendarInput!]!) {
    updatePriceCalendar(carId: $carId, updatePriceCalendarData: $updatePriceCalendarData) {
      id
      priceCalendar {
        date
        price
        isAvailable
      }
    }
  }
`;

export const UPDATE_HOURLY_PRICE_CALENDAR = gql`
  mutation UpdateHourlyPriceCalendar(
    $carId: String!
    $updatePriceCalendarData: [PriceCalendarInput!]!
  ) {
    updateHourlyPriceCalendar(carId: $carId, updatePriceCalendarData: $updatePriceCalendarData) {
      id
      priceCalendar {
        date
        price
        hourPrice
        isAvailable
      }
    }
  }
`;

export const UPDATE_CAR_HOURLY_PRICING = gql`
  mutation UpdateCarHourlyPricing($carId: String!, $input: HourlyPricingUpdateInput!) {
    updateCarHourlyPricing(carId: $carId, input: $input) {
      id
      hourlyRentalIsOn
      weekdayHrPrice
      weekendHrPrice
      holidayHrPrice
      priceCalendar {
        date
        price
        hourPrice
        isAvailable
      }
    }
  }
`;

export const GET_PRESIGNED_URL = gql`
  mutation GetPresignedUrl($fileType: String!, $carId: String!) {
    getPresignedUrl(fileType: $fileType, carId: $carId) {
      url
      key
    }
  }
`;

export const GET_PRESIGNED_URLS = gql`
  mutation GetPresignedUrls($fileType: String!, $carId: String!, $count: Float!) {
    getPresignedUrls(fileType: $fileType, carId: $carId, count: $count) {
      url
      key
    }
  }
`;

export const UPDATE_CAR_DISCOUNT = gql`
  mutation UpdateCarDiscount($carId: String!, $discountData: DiscountInput!) {
    updateCarDiscount(carId: $carId, discountData: $discountData) {
      id
      discount {
        lastMinBooth
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
      }
    }
  }
`;

export const UPDATE_CAR_TRIP_PREFERENCES = gql`
  mutation UpdateCarTripPreferences($carId: String!, $tripPreferencesData: TripPreferencesInput!) {
    updateCarTripPreferences(carId: $carId, tripPreferences: $tripPreferencesData) {
      id
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
    }
  }
`;

export const UPDATE_DISTANCE_LIMIT = gql`
  mutation UpdateDistanceLimit($carId: String!, $distanceLimit: DistanceLimitInput!) {
    updateDistanceLimit(carId: $carId, distanceLimit: $distanceLimit) {
      id
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
    }
  }
`;

export const UPDATE_CAR_IMAGES = gql`
  mutation UpdateCarImages($carId: String!, $updateImagesData: UpdateImagesInput!) {
    updateCarImages(carId: $carId, updateImagesData: $updateImagesData) {
      id
      thumbnailUrl
      images {
        imageUrl
      }
    }
  }
`;

export const GET_CAR_BY_LOCATION_ID = gql`
  query CarsByLocation($locationId: Int!, $type: ProductType) {
    carsByLocation(locationId: $locationId, type: $type) {
      id
      name
      displayName
      weekdayPrice
      weekendPrice
      holidayPrice
      monthlyPrice
      monthlyRentalIsOn
      monthlyPrice2Months
      monthlyPrice3Months
      gas
      gearType
      thumbnailUrl
      year
      model
      make
      seats
      images {
        imageUrl
      }
      priceCalendar {
        date
        price
        isAvailable
      }
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      isCreated
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
      dailyTripFee
      ageLimit
      features
      category
      latitude
      longitude
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
    }
  }
`;

export const FIND_AVAILABLE_CARS = gql`
  query FindAvailableCars($input: FindAvailableCarsInput!) {
    availableCars(input: $input) {
      id
      make
      model
      year
      category
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
      weekdayPrice
      weekendPrice
      priceCalendar {
        date
        price
        isAvailable
      }
      owner {
        id
        firstName
        lastName
        displayName
        email
      }
      latitude
      longitude
      location {
        id
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
        taxRate
      }
      images {
        imageUrl
      }
      calculatedPrice {
        totalDays
        pricePerDay
        carPricePerTrip
        tripFeeTotal
        youngsterFeeTotal
        deliveryFee
        taxRate
        tax
        totalPrice
        totalPriceWithDelivery
        rentalType
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountPercentage
        discountAmount
        discountTier
      }
      minimumRentalDays
      maximumRentalDays
      isFavorite
    }
  }
`;

export const FIND_AVAILABLE_CARS_WITH_DELIVERY = gql`
  query FindAvailableCarsWithDelivery($input: FindAvailableCarsWithDeliveryInput!) {
    availableCarsWithDelivery(input: $input) {
      isAirportSearch
      airportContext {
        id
        name
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
      }
      atAirportCars {
        id
        make
        model
        year
        category
        productTypeDetails {
          id
          type
          saleAs
          hasInsurance
          hasTripFee
          hasYoungsterFee
          taxable
          isOn
        }
        weekdayPrice
        weekendPrice
        priceCalendar {
          date
          price
          isAvailable
        }
        owner {
          id
          firstName
          lastName
          displayName
          email
        }
        latitude
        longitude
        location {
          id
          address
          city
          state
          zipCode
          timezone
          latitude
          longitude
          taxRate
        }
        images {
          imageUrl
        }
        calculatedPrice {
          totalDays
          pricePerDay
          carPricePerTrip
          tripFeeTotal
          youngsterFeeTotal
          deliveryFee
          taxRate
          tax
          totalPrice
          totalPriceWithDelivery
          rentalType
          discountPercentage
          discountAmount
          discountTier
        }
        minimumRentalDays
        maximumRentalDays
        isFavorite
        isNew
      }
      offAirportCars {
        id
        make
        model
        year
        category
        productTypeDetails {
          id
          type
          saleAs
          hasInsurance
          hasTripFee
          hasYoungsterFee
          taxable
          isOn
        }
        weekdayPrice
        weekendPrice
        priceCalendar {
          date
          price
          isAvailable
        }
        owner {
          id
          firstName
          lastName
          displayName
          email
        }
        latitude
        longitude
        location {
          id
          address
          city
          state
          zipCode
          timezone
          latitude
          longitude
          taxRate
        }
        images {
          imageUrl
        }
        calculatedPrice {
          totalDays
          pricePerDay
          carPricePerTrip
          tripFeeTotal
          youngsterFeeTotal
          deliveryFee
          taxRate
          tax
          totalPrice
          totalPriceWithDelivery
          rentalType
          discountPercentage
          discountAmount
          discountTier
        }
        minimumRentalDays
        maximumRentalDays
        isFavorite
        isNew
        distanceMiles
      }
    }
  }
`;

export const GET_CAR_MAKES_AND_MODELS = gql`
  query GetCarMakesAndModels {
    getAllCarMakesAndModels {
      id
      make
      model
      years
    }
  }
`;

export const GET_CAR_BOOKED_DATES = gql`
  query GetCarBookedDates($carId: String!, $excludeBookingId: String) {
    getCarBookedDates(carId: $carId, excludeBookingId: $excludeBookingId) {
      bookedTimeSlots {
        startAt
        endAt
      }
      tripBufferHours
    }
  }
`;

export const FIND_BOOKED_RECORD_OF_THE_CAR = gql`
  query FindBookedRecordOfTheCar($carId: String!, $excludeBookingId: String) {
    findBookedRecordOfTheCar(carId: $carId, excludeBookingId: $excludeBookingId) {
      id
      name
      displayName
      weekdayPrice
      weekendPrice
      holidayPrice
      monthlyPrice
      monthlyRentalIsOn
      monthlyPrice2Months
      monthlyPrice3Months
      gas
      gearType
      thumbnailUrl
      year
      model
      make
      seats
      images {
        imageUrl
      }
      priceCalendar {
        date
        price
        isAvailable
      }
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      isCreated
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      category
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
      deposit
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      bookings {
        id
        startAt
        endAt
        guestId
        hostId
        pickupLocationId
        returnLocationId
        receipt {
          pricePerDay
          totalDays
          totalPrice
          extraMilages
          perExtraMileCost
          tripFee
          insurancePerDay
          insuranceByTrip
          subtotal
          paymentMethod
          taxRate
          tax
          balance
          last4digitOfCard
        }
        status
      }
      availabilities {
        id
        startAt
        endAt
        reason
        note
        createdAt
      }
      dailyTripFee
      createdAt
      ageLimit
      category
    }
  }
`;

export const SET_CAR_DELIVERY_LOCATION = gql`
  mutation SetCarDeliveryLocation($input: CarDeliveryLocationInput!) {
    setCarDeliveryLocation(input: $input) {
      id
      fee
      isActive
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
      }
    }
  }
`;

export const GET_CAR_DELIVERY_LOCATIONS = gql`
  query GetCarDeliveryLocations($carId: String!) {
    getCarDeliveryLocations(carId: $carId) {
      id
      fee
      isActive
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
      }
    }
  }
`;

export const REMOVE_CAR_DELIVERY_LOCATION = gql`
  mutation RemoveCarDeliveryLocation($input: RemoveCarDeliveryLocationInput!) {
    removeCarDeliveryLocation(input: $input)
  }
`;

/** Host My Listings cards only — no pricing, product type, or booking status. */
export const GET_MY_CARS_V2 = gql`
  query GetMyCarsV2($input: GetMyCarsV2Input!) {
    getMyCarsV2(input: $input) {
      totalCount
      hasMore
      page
      limit
      totalPages
      items {
        id
        make
        model
        year
        category
        displayName
        thumbnailUrl
        status
        licensePlate
        location {
          timezone
          city
        }
        bookings {
          id
          startAt
          endAt
        }
      }
    }
  }
`;

/** Host home carousel (`Car` component) — prices + category; no listing-card extras. */
export const GET_MY_CARS_V2_HOME = gql`
  query GetMyCarsV2Home($input: GetMyCarsV2Input!) {
    getMyCarsV2(input: $input) {
      totalCount
      hasMore
      page
      limit
      totalPages
      items {
        id
        make
        model
        year
        category
        thumbnailUrl
        isCreated
        weekdayPrice
        monthlyPrice
        bookings {
          id
          startAt
          endAt
        }
      }
    }
  }
`;

/** Lean page for pickers (earnings / swap) — omits bookings to avoid N+1 ResolveField work. */
export const GET_MY_CARS_V2_LEAN = gql`
  query GetMyCarsV2Lean($input: GetMyCarsV2Input!) {
    getMyCarsV2(input: $input) {
      totalCount
      hasMore
      page
      limit
      totalPages
      items {
        id
        make
        model
        year
        displayName
        thumbnailUrl
      }
    }
  }
`;

export const GET_ALL_MY_CARS = gql`
  query GetMyCars {
    getMyCars {
      id
      name
      displayName
      weekdayPrice
      weekendPrice
      holidayPrice
      monthlyPrice
      monthlyRentalIsOn
      monthlyPrice2Months
      monthlyPrice3Months
      gas
      gearType
      thumbnailUrl
      year
      model
      make
      seats
      images {
        imageUrl
      }
      priceCalendar {
        date
        price
        isAvailable
      }
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      isCreated
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      category
      discount {
        threeDays
        oneWeek
        twoWeeks
        threeWeeks
        oneMonth
        twoMonths
        threeMonths
        lastMinBooth
      }
      tripPreferences {
        advanceNoticeHours
        tripBufferHours
        shortestTripDays
        longestTripDays
      }
      distanceLimit {
        dailyDistanceLimit
        unlimitedMileage
        monthlyDistanceLimit
        monthlyUnlimitedMileage
      }
      deposit
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      bookings {
        id
        startAt
        endAt
        guestId
        hostId
        pickupLocationId
        returnLocationId
        receipt {
          pricePerDay
          totalDays
          totalPrice
          extraMilages
          perExtraMileCost
          tripFee
          insurancePerDay
          insuranceByTrip
          subtotal
          paymentMethod
          taxRate
          tax
          balance
          last4digitOfCard
        }
        status
      }
      dailyTripFee
      createdAt
      ageLimit
      category
      status
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
    }
  }
`;

export const GET_CARS_BY_DEALER_ID = gql`
  query GetCarsByDealer($input: GetDealerCarsInput!) {
    getAllCarsOnSaleByDealer(input: $input) {
      id
      name
      displayName
      gas
      gearType
      thumbnailUrl
      year
      model
      make
      seats
      images {
        imageUrl
      }
      vin
      sold
      soldAt
      currentLocation
      originalPrice
      listedSalePrice
      isCreated
      licensePlate
      licensePlateState
      gasGrade
      cityMpg
      hwyMpg
      doors
      description
      listedSalePrice
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      dailyTripFee
      createdAt
      ageLimit
      features
      category
      mileage
      odometerUnit
      dealer {
        id
        name
        email
        phone
        streetAddress
        city
        state
        zipCode
      }
      status
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
    }
  }
`;

export const CALCULATE_CAR_PRICING = gql`
  query CalculateCarPricingResolver($input: CarPricingInput!) {
    calculateCarPricingResolver(input: $input) {
      id
      make
      model
      year
      calculatedPrice {
        totalDays
        pricePerDay
        carPricePerTrip
        tripFeeTotal
        youngsterFeeTotal
        taxRate
        tax
        totalPrice
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountPercentage
        discountAmount
        discountTier
      }
    }
  }
`;

export const CALCULATE_CAR_PRICING_WITHOUT_TAX = gql`
  query CalculateCarPricingWithoutTaxResolver($input: CarPricingInput!) {
    calculateCarPricingWithoutTaxResolver(input: $input) {
      id
      make
      model
      year
      calculatedPrice {
        totalDays
        pricePerDay
        carPricePerTrip
        tripFeeTotal
        youngsterFeeTotal
        totalPrice
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountPercentage
        discountAmount
        discountTier
      }
    }
  }
`;

export const GET_CARS_NEAR_ME = gql`
  query GetCarsNearMe($input: GetCarsNearMeInput!) {
    getCarsNearMe(input: $input) {
      id
      make
      model
      year
      category
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
      seats
      weekdayPrice
      weekendPrice
      holidayPrice
      thumbnailUrl
      latitude
      longitude
      images {
        imageUrl
      }
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        latitude
        longitude
        taxRate
      }
      owner {
        id
        firstName
        lastName
        displayName
        email
      }
      calculatedPrice {
        totalDays
        pricePerDay
        carPricePerTrip
        tripFeeTotal
        youngsterFeeTotal
        totalPrice
        lastMinSurchargePercentage
        lastMinSurchargeAmount
        discountPercentage
        discountAmount
        discountTier
      }
      minimumRentalDays
      maximumRentalDays
      distanceMiles
      isFavorite
    }
  }
`;

/**
 * Spec 022 D14: "Weekly rentals near you" on a brand whose cards show the week price. Every car
 * near the guest (not only monthly ones), with the card fields and the week price.
 */
export const GET_WEEKLY_RENTAL_CARS_NEAR_ME = gql`
  query GetWeeklyRentalCarsNearMe($input: GetCarsNearMeInput!) {
    getCarsNearMe(input: $input) {
      id
      make
      model
      year
      category
      weekdayPrice
      monthlyPrice
      monthlyRentalIsOn
      thumbnailUrl
      isCreated
      tripSettings {
        maximumRentalDays
        displayPrices {
          oneWeek
        }
      }
    }
  }
`;

export const GET_MONTHLY_RENTAL_CARS_NEAR_ME = gql`
  query GetMonthlyRentalCarsNearMe($input: GetCarsNearMeInput!) {
    getMonthlyRentalCarsNearMe(input: $input) {
      id
      make
      model
      year
      category
      weekdayPrice
      monthlyPrice
      monthlyRentalIsOn
      thumbnailUrl
      isCreated
    }
  }
`;

// Favorites queries and mutations
export const ADD_FAVORITE = gql`
  mutation AddFavorite($input: AddFavoriteInput!) {
    addFavorite(input: $input) {
      id
      userId
      carId
      createdAt
    }
  }
`;

export const REMOVE_FAVORITE = gql`
  mutation RemoveFavorite($input: RemoveFavoriteInput!) {
    removeFavorite(input: $input)
  }
`;

export const GET_MY_FAVORITES = gql`
  query GetMyFavorites {
    getMyFavorites {
      id
      name
      displayName
      weekdayPrice
      weekendPrice
      holidayPrice
      monthlyPrice
      monthlyRentalIsOn
      monthlyPrice2Months
      monthlyPrice3Months
      gas
      gearType
      thumbnailUrl
      year
      model
      make
      seats
      images {
        imageUrl
      }
      location {
        id
        name
        address
        city
        state
        zipCode
        timezone
        taxRate
      }
      owner {
        id
        email
        firstName
        lastName
        displayName
      }
      productTypeDetails {
        id
        type
        saleAs
        hasInsurance
        hasTripFee
        hasYoungsterFee
        taxable
        isOn
      }
    }
  }
`;
export const GET_CAR_AVAILABILITIES_BY_CAR = gql`
  query CarAvailabilitiesByCar($carId: String!) {
    carAvailabilitiesByCar(carId: $carId) {
      id
      carId
      hostId
      startAt
      endAt
      reason
      note
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_CAR_AVAILABILITY = gql`
  mutation CreateCarAvailability($input: CreateCarAvailabilityInput!) {
    createCarAvailability(input: $input) {
      id
      carId
      hostId
      startAt
      endAt
      reason
      note
      createdAt
      updatedAt
    }
  }
`;

export const REMOVE_CAR_AVAILABILITY = gql`
  mutation RemoveCarAvailability($id: String!) {
    removeCarAvailability(id: $id)
  }
`;

/**
 * Remove blocked dates by date strings array
 * Backend should:
 * Remove blocked dates by date ranges (absolute UTC timestamps)
 */
export const REMOVE_CAR_AVAILABILITIES_BY_DATES = gql`
  mutation RemoveCarAvailabilitiesByDates($carId: String!, $ranges: [AvailabilityPeriodInput!]!) {
    removeCarAvailabilitiesByDates(carId: $carId, ranges: $ranges) {
      success
      removedCount
      message
    }
  }
`;

/**
 * Remove all blocked dates for a car
 */
export const REMOVE_ALL_CAR_AVAILABILITIES = gql`
  mutation RemoveAllCarAvailabilities($carId: String!) {
    removeAllCarAvailabilities(carId: $carId) {
      success
      removedCount
      message
    }
  }
`;

/**
 * Create multiple unavailability periods
 */
export const CREATE_CAR_AVAILABILITIES_BULK = gql`
  mutation CreateCarAvailabilitiesBulk($input: CreateCarAvailabilitiesBulkInput!) {
    createCarAvailabilitiesBulk(input: $input) {
      success
      createdCount
      message
      availabilities {
        id
        carId
        hostId
        startAt
        endAt
        reason
        note
        createdAt
        updatedAt
      }
    }
  }
`;

/**
 * D40: the server decides where checkout happens for this car with the chosen
 * pickup and return. Null = stay on this brand; a domain = redirect there;
 * `AIRPORT_NOT_BOOKABLE` = no brand taking part has every chosen airport.
 */
export const GET_CAR_CHECKOUT_BRAND_SITE = gql`
  query GetCarCheckoutBrandSite($id: String!, $pickupLocationId: Int, $returnLocationId: Int) {
    car(id: $id) {
      id
      checkoutBrandSite(pickupLocationId: $pickupLocationId, returnLocationId: $returnLocationId)
    }
  }
`;

/** Spec 028: cars free for the same trip moved a few days ("similar dates"). */
export const SIMILAR_DATE_CARS = gql`
  query SimilarDateCars($input: SimilarDateCarsInput!) {
    similarDateCars(input: $input) {
      id
      make
      model
      year
      weekdayPrice
      latitude
      longitude
      location {
        id
        city
        state
        timezone
        latitude
        longitude
      }
      images {
        imageUrl
      }
      calculatedPrice {
        totalDays
        pricePerDay
        carPricePerTrip
        tripFeeTotal
        youngsterFeeTotal
        deliveryFee
        taxRate
        tax
        totalPrice
        totalPriceWithDelivery
        rentalType
        discountPercentage
        discountAmount
        discountTier
      }
      isNew
      distanceMiles
      shiftDays
      similarStartAt
      similarEndAt
    }
  }
`;

/**
 * Spec 028: the unit a car's distance limits are stored in. Its own query
 * (errors ignored), so an API without the field never breaks the car page.
 */
export const CAR_DISTANCE_LIMIT_UNIT = gql`
  query CarDistanceLimitUnit($id: String!) {
    car(id: $id) {
      id
      odometerUnit
      distanceLimit {
        unit
      }
    }
  }
`;
